import { Box, useMediaQuery } from "@mui/material";
import { type FC, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useRoleColors } from "@/hooks/useRoleColors";
import { STAFF_PICKER } from "@/theme/tokens";
import { SystemDropdown } from "@/ui/Inputs/SystemDropDown";
import type { DropdownItem } from "@/ui/Inputs/SystemDropDown/types";
import { JobSelector } from "@/ui/molecules/JobSelector";
import { StepHeader } from "@/ui/molecules/StepHeader";
import type { RoleColorResolver } from "@/utils/categoricalColors";
import { scheduleSlotDay } from "@/utils/departmentWeeks";
import type { Nurse, NurseFullRole, WeekData } from "@/utils/models";
import { inverseRolesMap, nurseRoleDisplayName } from "@/utils/utilFunctions";
import { EditTimePopover } from "../EditTimePopover";
import { LocationPicker, type LocationPickerSelectionMeta } from "../LocationPicker/LocationPicker";
import { LockedShiftSummary, ShiftCards, type ShiftCardsDensity } from "./ShiftCards";
import type { AddStaffShift } from "./useAddStaffShift";

type Props = {
	shift: AddStaffShift;
	rolesToDisplay: NurseFullRole[];
	requestJobId: string | undefined;
	selectedRoom: string;
	onRoomChange: (location: string, meta: LocationPickerSelectionMeta) => void;
	hideRoomSelect: boolean;
	/** The first picked nurse — the location picker ranks the locations they work most. */
	focusNurse: Nurse | null;
	/** The panel's schedule weeks; `weekIndex` and `dayNo` are positions in them. */
	weeks: WeekData[];
	weekIndex: number;
	dayNo: number;
	showActual: boolean;
};

/**
 * Each Job / Location field in the step heading's trailing slot: its max width as the size it asks for (so the
 * heading row knows whether both fit beside "Which shift?"), shrinking to the min before the pair wraps under it.
 */
const ScopeField: FC<{ children: ReactNode }> = ({ children }) => (
	<Box sx={{ width: STAFF_PICKER.SCOPE_FIELD_MAX_WIDTH, minWidth: STAFF_PICKER.SCOPE_FIELD_MIN_WIDTH, flexShrink: 1 }}>{children}</Box>
);

/**
 * A role-mode department's roles as dropdown rows, each with its role colour. Only mapped roles, as the
 * role chips this replaces offered (an unmapped token has no short name to show).
 */
const toRoleItems = (roles: NurseFullRole[], roleColor: RoleColorResolver): DropdownItem<NurseFullRole>[] =>
	roles
		.filter((role) => role in inverseRolesMap)
		.map((role) => ({
			value: role,
			// DEFAULT: a role without a display name reads as its short code, as the role chips always showed it.
			label: nurseRoleDisplayName[inverseRolesMap[role]] ?? inverseRolesMap[role],
			color: roleColor(role).dot,
		}));

/** Job dropdown (job mode), role dropdown (role mode), or the request's fixed job as a read-only field. */
const ScheduleTargetField: FC<Pick<Props, "shift" | "rolesToDisplay" | "requestJobId">> = ({ shift, rolesToDisplay, requestJobId }) => {
	const roleColor = useRoleColors();
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	if (shift.mode === "job" && requestJobId) {
		const job = chosenDepartment.jobs?.find((candidate) => candidate.id === requestJobId);
		if (!job) return null;
		// The request fixes the job: shown as the same field, locked, so the row reads the same either way.
		return (
			<ScopeField>
				<SystemDropdown
					data-testid="add-nurse-request-job"
					label={t("job")}
					items={[{ value: job.id, label: job.displayName }]}
					selectedItem={job.id}
					disabled
					fullWidth
				/>
			</ScopeField>
		);
	}
	if (shift.mode === "job") {
		if (shift.jobs.length === 0) return null;
		return (
			<ScopeField>
				<JobSelector
					jobs={shift.jobs}
					selectedJobIds={shift.selectedJobId ? [shift.selectedJobId] : []}
					// A shift always has a job, so an emptied pick is ignored.
					onSelectedJobsChanged={(ids) => ids[0] && shift.selectJob(ids[0])}
					singleSelect
					hideAllOption
					variant="dropdown"
					label={t("job")}
					fullWidth
					dataTestId="add-nurse-job-dropdown"
				/>
			</ScopeField>
		);
	}
	if (rolesToDisplay.length < 2) return null;
	return (
		<ScopeField>
			<SystemDropdown<NurseFullRole>
				data-testid="add-nurse-role-dropdown"
				label={t("add.staff.role")}
				items={toRoleItems(rolesToDisplay, roleColor)}
				selectedItem={shift.selectedRole}
				onChange={(role) => role && shift.selectRole(role)}
				fullWidth
			/>
		</ScopeField>
	);
};

/**
 * Who the shift is for (job or role) and where (location): step 1's scope, at the end of its heading row — they
 * scope the cards rather than answer the step, and sharing the heading's row leaves the staff list a row's height.
 */
const ScopeFields: FC<Props> = ({
	shift,
	rolesToDisplay,
	requestJobId,
	selectedRoom,
	onRoomChange,
	hideRoomSelect,
	focusNurse,
	weeks,
	weekIndex,
	dayNo,
	showActual,
}) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	return (
		<>
			<ScheduleTargetField shift={shift} rolesToDisplay={rolesToDisplay} requestJobId={requestJobId} />
			{!hideRoomSelect && (
				<ScopeField>
					<LocationPicker
						data-testid="add-nurse-location-dropdown"
						label={t("location")}
						locations={chosenDepartment.locations}
						selectedLocation={selectedRoom}
						nurseId={focusNurse?.id ?? null}
						nurseFirstName={focusNurse?.firstName}
						parDay={{ ...scheduleSlotDay(weeks, { weekIndex, shiftIndex: shift.shiftNo, dayIndex: dayNo }), isActual: showActual }}
						clearable
						onChange={onRoomChange}
					/>
				</ScopeField>
			)}
		</>
	);
};

/** The custom-hours popover, anchored on the Custom card. */
const CustomHoursPopover: FC<{ shift: AddStaffShift; anchorEl: HTMLElement; onClose: () => void }> = ({ shift, anchorEl, onClose }) => {
	// DEFAULT: nothing picked yet, so the custom-hours popover opens on the cell's own default hours.
	const seedWindow = shift.window ?? shift.defaultWindow;
	return (
		<EditTimePopover
			role={shift.selectedRole ? inverseRolesMap[shift.selectedRole] : undefined}
			jobId={shift.mode === "job" ? shift.selectedJobId : undefined}
			startTime={seedWindow.start}
			endTime={seedWindow.end}
			onTimeChange={({ newStartTime, newEndTime }) => shift.pickWindow({ start: newStartTime, end: newEndTime })}
			onClose={onClose}
			anchorEl={anchorEl}
			isActual={false}
			requireNonZeroLength
		/>
	);
};

/**
 * Step 1 · "Which shift?" — who it's for (job or role) and where (location) at the end of the heading row, then
 * the shift cards (or a request's locked shift), one line each on a short screen. Changing the job re-derives
 * the cards; everything in step 2 re-derives from what is picked here.
 */
export const ShiftStep: FC<Props> = (props) => {
	const { shift } = props;
	const { t } = useTranslation();
	const [customAnchor, setCustomAnchor] = useState<HTMLElement | null>(null);
	const cardDensity: ShiftCardsDensity = useMediaQuery(STAFF_PICKER.COMPACT_VIEWPORT_QUERY) ? "inline" : "comfortable";

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, [STAFF_PICKER.COMPACT_VIEWPORT_QUERY]: { gap: 1 } }} data-testid="add-nurse-shift-step">
			<StepHeader
				step={1}
				title={t("add.staff.step.shift")}
				trailing={hasScopeFields(props) ? <ScopeFields {...props} /> : undefined}
				dataTestId="add-nurse-step-shift"
			/>
			{shift.isLocked && shift.window ? (
				<LockedShiftSummary window={shift.window} density={cardDensity} />
			) : (
				<ShiftCards
					options={shift.cardOptions}
					selectedKey={shift.selectedCardKey}
					window={shift.window}
					onPickPreset={shift.pickWindow}
					onCustomClick={setCustomAnchor}
					onClearCustom={shift.clearWindow}
					density={cardDensity}
				/>
			)}
			{customAnchor && !shift.isLocked && <CustomHoursPopover shift={shift} anchorEl={customAnchor} onClose={() => setCustomAnchor(null)} />}
		</Box>
	);
};

/** Whether step 1 has any scope field to show: a job/role choice or the location picker. */
const hasScopeFields = ({ shift, rolesToDisplay, requestJobId, hideRoomSelect }: Props): boolean => {
	const hasTarget = shift.mode === "job" ? shift.jobs.length > 0 || !!requestJobId : rolesToDisplay.length > 1;
	return hasTarget || !hideRoomSelect;
};
